import { CheckBox, NumberBox, TextBox } from "devextreme-react";
import { CON_SOURCE, DataItem, HCostData } from "@constants/inbound";
import { useAtom } from "jotai";
import { CommonUtil } from "@utils/CommonUtil";
import { hcostInboundAtom } from "@/store/hcost-atom";
import React, { useMemo, useState } from "react";
import { CommonConst } from "@constants/common-const";
import AlertModal from "@components/common/alert-modal";
import Radio from "@components/common/radio";
import useCounselHook from "@feature/counsel/useCounselHook";
import CounselDesignItemRadio from "@feature/counsel/update/counsel-design-radio";
import TooltipComponent from "@components/common/tooltip";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

type Props = {
  accessType: string;
};

export type CounselDesignItemTree = CounselModelItem & {
  children: CounselDesignItemTree[];
};

const CounselPopupDetailV3 = ({ accessType }: Props) => {
  const [hCostResult, setHCostResult] = useAtom(hcostInboundAtom);
  const [selectedIndex, _] = useState(0);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [facilityCode, setFacilityCode] = useState(null);
  const { onValueChangeCounselHandler, getDesignModel } = useCounselHook();
  const visibleCost = () => {
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      return true;
    } else if (accessType !== CommonConst.ACCESS_TYPE_ADMIN && hCostResult.conSource?.code === CON_SOURCE.FARMMORNING) {
      return false;
    }
    return true;
  };

  const checkDisabled = (data: CounselModelItem) => {
    if (!data) return;
    const categoryData = hCostResult.counselDesigns[0].counselDesignItems.filter(
      v => v.category1?.code === data.category1?.code,
    );
    return categoryData.every(v => v.enabled === false);
  };

  const createDataTree = useMemo(() => {
    if (!hCostResult.counselDesigns[0].counselDesignItems) return;
    const hashTable = Object.create(null);
    hCostResult.counselDesigns[0].counselDesignItems.forEach(
      aData => (hashTable[aData.itemNumber] = { ...aData, children: [] }),
    );
    const dataTree: CounselDesignItemTree[] = [];
    hCostResult.counselDesigns[0].counselDesignItems.forEach(aData => {
      if (aData.parentItemNumber) hashTable[aData.parentItemNumber].children.push(hashTable[aData.itemNumber]);
      else dataTree.push(hashTable[aData.itemNumber]);
    });
    return dataTree;
  }, [hCostResult.counselDesigns]);

  const conFacilityTypeChanged = (e: any) => {
    if (e.target.value === hCostResult.counselDesigns[selectedIndex].facilityType.code) return;

    setHCostResult(draft => {
      if (
        !draft.counselDesigns[selectedIndex].facilityType ||
        draft.counselDesigns[selectedIndex].facilityType?.code !== e.target.value
      ) {
        draft.counselDesigns[selectedIndex].facilityType = HCostData.conFacilityType.items.find(
          (val: DataItem) => val.code === e.target.value,
        );
        getDesignModel(e.target.value);
      } else if (draft.counselDesigns[selectedIndex].facilityType.code === e.target.value) {
        draft.counselDesigns[selectedIndex].facilityType = null;
      } else {
      }
    });
  };

  const checkBoxHandler = (e: any, item: CounselDesignItemTree) => {
    if (!e.event) return;
    if (e.value) {
      setHCostResult(prev => {
        prev.counselDesigns[0].counselDesignItems.find(
          (v: CounselDesignItem) => v.itemNumber === item.itemNumber,
        ).enabled = true;

        // parentItemNumber 처리
        if (item.parentItemNumber) {
          prev.counselDesigns[0].counselDesignItems.find(
            (v: CounselDesignItem) => v.itemNumber === item.parentItemNumber,
          ).enabled = true;
        }
        prev.isCostSimpleFlag = true;
      });
    } else {
      setHCostResult(prev => {
        prev.counselDesigns[0].counselDesignItems.find(
          (v: CounselDesignItem) => v.itemNumber === item.itemNumber,
        ).enabled = false;

        // children 처리
        const children = hCostResult.counselDesigns[0].counselDesignItems.filter(
          v => v.parentItemNumber === item.itemNumber,
        );
        if (children) {
          children.forEach(child => {
            prev.counselDesigns[0].counselDesignItems.find(
              (v: CounselDesignItem) => v.itemNumber === child.itemNumber,
            ).enabled = false;
            const grandChildren = prev.counselDesigns[0].counselDesignItems.filter(
              v => v.parentItemNumber === child.itemNumber,
            );
            grandChildren?.forEach(grand => {
              prev.counselDesigns[0].counselDesignItems.find(
                (v: CounselDesignItem) => v.itemNumber === grand.itemNumber,
              ).enabled = false;
            });
          });
        }

        // parentItemNumber 처리
        const parent = prev.counselDesigns[0].counselDesignItems.filter(
          v => v.parentItemNumber === item.parentItemNumber,
        );
        if (parent && parent.every(v => v.enabled === false)) {
          prev.counselDesigns[0].counselDesignItems.find(v => v.itemNumber === item.parentItemNumber).enabled = false;
        }

        prev.isCostSimpleFlag = true;
      });
    }
  };

  const alertCallback = (isConfirmed: boolean) => {
    setAlertModalVisible(false);
    if (isConfirmed) {
      conFacilityTypeChanged(facilityCode);
    }
  };

  return (
    <>
      <div className="content-fieldset">
        <h3 className="content-fieldset-title">설계 정보</h3>
        <div className="content-fieldset-title-desc" />
        <div className="content-field row">
          <div className="content-field-label col-12 col-sm-4 col-md-3 col-lg-2">
            <div className="content-label-name">용도/작물</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            <TextBox
              value={hCostResult.conUsage}
              showClearButton
              onValueChanged={onValueChangeCounselHandler((draft, value) => {
                draft.conUsage = value;
              })}
            />
            <div className="content-value-desc" />
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">시설 유형</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            <div className="flex flex-wrap gap-y-2 gap-x-3 mb-2">
              {HCostData.conFacilityType.items.map((v: DataItem) => (
                <Radio
                  labelClassName="!text-sm"
                  key={v.value}
                  label={v.value}
                  value={v.code}
                  data-cy="conFacilityType"
                  name={"conFacilityType"}
                  dataCy={v.value}
                  onClick={e => {
                    setFacilityCode(e);
                    setAlertModalVisible(true);
                  }}
                  checked={hCostResult.counselDesigns[selectedIndex].facilityType?.code === v.code}
                  readOnly={true}
                ></Radio>
              ))}
            </div>
            <div className="content-value-desc" />
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">시설 규격</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full flex gap-x-2">
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.conWidthBay.data.unit}
                max={HCostData.conWidthBay.data.max}
                min={HCostData.conWidthBay.data.min}
                showSpinButtons={true}
                step={HCostData.conWidthBay.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.widthBay}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.widthBay = value;
                  draft.counselDesigns[selectedIndex].counselDesignBasic.width =
                    value * draft.counselDesigns[selectedIndex].counselDesignBasic.bay;

                  draft.counselDesigns[selectedIndex].counselDesignBasic.areaPyeong = CommonUtil.round(
                    (value *
                      draft.counselDesigns[selectedIndex].counselDesignBasic.length *
                      draft.counselDesigns[selectedIndex].counselDesignBasic.bay) /
                      3.3058,
                    0,
                  );
                })}
              />
              <div className="content-value-desc">한동 폭</div>
              <div className="mt-5">
                <NumberBox
                  format={"#,##0.#" + HCostData.conWidth.data.unit}
                  max={HCostData.conWidth.data.max}
                  min={HCostData.conWidth.data.min}
                  showSpinButtons={true}
                  step={HCostData.conWidth.data.step}
                  value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.width}
                  onValueChanged={onValueChangeCounselHandler((draft, value) => {
                    draft.counselDesigns[selectedIndex].counselDesignBasic.width = value;
                  })}
                />
                <div className="content-value-desc">(시설 폭)</div>
              </div>
            </div>
            <div className="pt-2">x</div>
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.conLength.data.unit}
                max={HCostData.conLength.data.max}
                min={HCostData.conLength.data.min}
                showSpinButtons={true}
                step={HCostData.conLength.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.length}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.length = value;
                  draft.counselDesigns[selectedIndex].counselDesignBasic.areaPyeong = CommonUtil.round(
                    (value *
                      draft.counselDesigns[selectedIndex].counselDesignBasic.widthBay *
                      draft.counselDesigns[selectedIndex].counselDesignBasic.bay) /
                      3.3058,
                    0,
                  );
                })}
              />
              <div className="content-value-desc">시설 길이</div>
            </div>
            <div className="pt-2">x</div>
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.basicBay.data.unit}
                max={HCostData.basicBay.data.max}
                min={HCostData.basicBay.data.min}
                showSpinButtons={true}
                step={HCostData.basicBay.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.bay}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.bay = value;
                  draft.counselDesigns[selectedIndex].counselDesignBasic.width =
                    value * draft.counselDesigns[selectedIndex].counselDesignBasic.widthBay;
                  draft.counselDesigns[selectedIndex].counselDesignBasic.areaPyeong = CommonUtil.round(
                    (value *
                      draft.counselDesigns[selectedIndex].counselDesignBasic.widthBay *
                      draft.counselDesigns[selectedIndex].counselDesignBasic.length) /
                      3.3058,
                    0,
                  );
                })}
              />
              <div className="content-value-desc">동수</div>
              <div className="mt-5">
                <NumberBox
                  format={"#,##0.#" + HCostData.level.data.unit}
                  max={HCostData.level.data.max}
                  min={HCostData.level.data.min}
                  showSpinButtons={true}
                  step={HCostData.level.data.step}
                  value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.level}
                  onValueChanged={onValueChangeCounselHandler((draft, value) => {
                    draft.counselDesigns[selectedIndex].counselDesignBasic.level = value;
                  })}
                />
                <div className="content-value-desc">중수</div>
              </div>
            </div>
            <div className="pt-2">=</div>
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.conAreaPyeong.data.unit}
                max={HCostData.conAreaPyeong.data.max}
                min={HCostData.conAreaPyeong.data.min}
                showSpinButtons={true}
                step={HCostData.conAreaPyeong.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.areaPyeong}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.areaPyeong = value;
                })}
              />
              <div className="content-value-desc">
                면적
                {` (${CommonUtil.round(
                  hCostResult.counselDesigns[selectedIndex].counselDesignBasic.widthBay *
                    hCostResult.counselDesigns[selectedIndex].counselDesignBasic.bay *
                    hCostResult.counselDesigns[selectedIndex].counselDesignBasic.length,
                  0,
                )}㎡)`}
              </div>
              <div className="mt-5">
                <NumberBox
                  format={"#,##0.#" + HCostData.basicHeightSide.data.unit}
                  max={HCostData.basicHeightSide.data.max}
                  min={HCostData.basicHeightSide.data.min}
                  showSpinButtons={true}
                  step={HCostData.basicHeightSide.data.step}
                  value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.heightSide}
                  onValueChanged={onValueChangeCounselHandler((draft, value) => {
                    draft.counselDesigns[selectedIndex].counselDesignBasic.heightSide = value;
                  })}
                />
                <div className="content-value-desc">측고</div>
              </div>
            </div>
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">(최소) 토지 규격</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full flex items-center gap-x-2">
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.conWidth.data.unit}
                max={HCostData.conWidth.data.max}
                min={HCostData.conWidth.data.min}
                showSpinButtons={true}
                step={HCostData.conWidth.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.landWidth}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.landWidth = value;
                  draft.counselDesigns[selectedIndex].counselDesignBasic.landPyeong = CommonUtil.round(
                    (value * draft.counselDesigns[selectedIndex].counselDesignBasic.landLength) / 3.3058,
                    0,
                  );
                })}
              />
              <div className="content-value-desc">토지 폭</div>
            </div>
            <div className="pb-5">x</div>
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.conLength.data.unit}
                max={HCostData.conLength.data.max}
                min={HCostData.conLength.data.min}
                showSpinButtons={true}
                step={HCostData.conLength.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.landLength}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.landLength = value;
                  draft.counselDesigns[selectedIndex].counselDesignBasic.landPyeong = CommonUtil.round(
                    (value * draft.counselDesigns[selectedIndex].counselDesignBasic.landWidth) / 3.3058,
                    0,
                  );
                })}
              />
              <div className="content-value-desc">토지 길이</div>
            </div>
            <div className="pb-5">=</div>
            <div>
              <NumberBox
                format={"#,##0.#" + HCostData.conAreaPyeong.data.unit}
                max={HCostData.conAreaPyeong.data.max}
                min={HCostData.conAreaPyeong.data.min}
                showSpinButtons={true}
                step={HCostData.conAreaPyeong.data.step}
                value={hCostResult.counselDesigns[selectedIndex]?.counselDesignBasic?.landPyeong}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselDesigns[selectedIndex].counselDesignBasic.landPyeong = value;
                })}
              />
              <div className="content-value-desc">
                면적
                {` (${CommonUtil.round(
                  hCostResult.counselDesigns[selectedIndex].counselDesignBasic.landWidth *
                    hCostResult.counselDesigns[selectedIndex].counselDesignBasic.landLength,
                  0,
                )}㎡)`}
              </div>
            </div>
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">시설 상세</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            {createDataTree.map((c1: CounselDesignItemTree) => (
              // category1
              <div key={c1.itemNumber} className={`pb-2 mb-1 border-b ${checkDisabled(c1) ? "!text-gray-250" : ""}`}>
                <div className="pb-1 font-bold"> {c1.itemName}</div>
                <div className="flex flex-wrap items-center gap-x-3 ml-4">
                  {c1.children.map((c2: CounselDesignItemTree) => (
                    <div key={c2.itemNumber} className={`${c2.children.length ? "w-full" : ""}`}>
                      <div>
                        <CheckBox
                          className={`mr-2 mb-1 ${checkDisabled(c1) ? "!text-gray-250" : ""}`}
                          key={c2.itemNumber}
                          text={c2.itemName}
                          value={
                            !!hCostResult.counselDesigns[selectedIndex]?.counselDesignItems?.find(
                              (val: CounselDesignItem) => val?.itemNumber === c2.itemNumber && val?.enabled,
                            )
                          }
                          onValueChanged={e => checkBoxHandler(e, c2)}
                        />
                      </div>
                      {c2.children.map((c3: CounselDesignItemTree) => (
                        <CounselDesignItemRadio key={c3.itemNumber} c2={c2} c3={c3} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="content-value-desc" />
          </div>
        </div>

        <TooltipComponent target="#houseCost" message="하우스 금액 = 건축 + 추가 + 환기 시설" />
        <div className="content-field">
          <div className="content-field-label">
            <div className="content-label-name flex justify-end" id="houseCost">
              <span>하우스 금액</span>
              <QuestionMarkCircleIcon className="w-4 ml-1" />
            </div>
            <div className="content-label-desc">
              <div>고객에게 제공</div>
              <div> / 최초 예상 금액</div>
            </div>
          </div>
          <div className="content-field-value full flex items-center gap-x-2">
            <div className="grow">
              {visibleCost() ? (
                <NumberBox
                  value={hCostResult.counselDesigns[selectedIndex].materialCost}
                  placeholder="자재비"
                  format="#,##0원"
                  inputAttr={{ class: "text-right" }}
                  onValueChanged={onValueChangeCounselHandler((draft, value) => {
                    draft.counselDesigns[selectedIndex].materialCost = value;
                  })}
                />
              ) : (
                <TextBox className="" value="자재비" readOnly={true}></TextBox>
              )}
              <div className="content-value-desc h-[41.9px]">
                {hCostResult?.counselDesigns[selectedIndex].materialCost ? (
                  <>
                    <div>{CommonUtil.formatNumber(hCostResult?.counselDesigns[selectedIndex].materialCost)}원 ~</div>
                    <div>
                      {CommonUtil.formatNumber(
                        hCostResult?.counselDesigns[selectedIndex].materialCost +
                          hCostResult?.counselDesigns[selectedIndex].materialCost * 0.1,
                      )}
                      원
                    </div>
                  </>
                ) : (
                  `0원`
                )}
              </div>
            </div>
            <div className="mb-5 h-[41.9px]">+</div>
            <div>
              {visibleCost() ? (
                <NumberBox
                  value={hCostResult.counselDesigns[selectedIndex].constructionCost}
                  placeholder="시공비"
                  format="#,##0원"
                  inputAttr={{ class: "text-right" }}
                  onValueChanged={onValueChangeCounselHandler((draft, value) => {
                    draft.counselDesigns[selectedIndex].constructionCost = value;
                  })}
                />
              ) : (
                <TextBox className="" value="시공비" readOnly={true}></TextBox>
              )}
              <div className="content-value-desc h-[41.9px]">
                {hCostResult?.counselDesigns[selectedIndex].constructionCost ? (
                  <>
                    <div>
                      {CommonUtil.formatNumber(hCostResult?.counselDesigns[selectedIndex].constructionCost)}원 ~
                    </div>
                    <div>
                      {CommonUtil.formatNumber(
                        hCostResult?.counselDesigns[selectedIndex].constructionCost +
                          hCostResult?.counselDesigns[selectedIndex].constructionCost * 0.1,
                      )}
                      원
                    </div>
                  </>
                ) : (
                  `0원`
                )}
              </div>
            </div>
            <div className="mb-5 h-[41.9px]">=</div>
            <div>
              {visibleCost() ? (
                <NumberBox
                  value={
                    hCostResult.counselDesigns[selectedIndex].materialCost +
                    hCostResult.counselDesigns[selectedIndex].constructionCost
                  }
                  placeholder="총 금액"
                  format="#,##0원"
                  inputAttr={{ class: "text-right" }}
                  readOnly
                />
              ) : (
                <TextBox className="" value="총 금액" readOnly={true}></TextBox>
              )}
              <div className="content-value-desc h-[41.9px]">
                {hCostResult.counselDesigns[selectedIndex].constructionCost ||
                hCostResult.counselDesigns[selectedIndex].materialCost ? (
                  <>
                    <div>
                      {CommonUtil.formatNumber(
                        hCostResult.counselDesigns[selectedIndex].materialCost +
                          hCostResult.counselDesigns[selectedIndex].constructionCost,
                      )}
                      원 ~
                    </div>
                    <div>
                      {CommonUtil.formatNumber(
                        hCostResult.counselDesigns[selectedIndex].materialCost +
                          hCostResult.counselDesigns[selectedIndex].constructionCost +
                          (hCostResult.counselDesigns[selectedIndex].materialCost +
                            hCostResult.counselDesigns[selectedIndex].constructionCost) *
                            0.1,
                      )}
                      원
                    </div>
                  </>
                ) : (
                  `0원`
                )}
              </div>
            </div>
          </div>
        </div>

        <TooltipComponent
          target="#totalCost"
          message="전체 금액 = 건축 + 추가 + 환기 + 관수 + 양액 + 냉난방 + 환경 + 기타 시설"
        />
        <div className="content-field">
          <div className="content-field-label">
            <div className="content-label-name flex justify-end" id="totalCost">
              <span>전체 금액</span>
              <QuestionMarkCircleIcon className="w-4 ml-1" />
            </div>
            <div className="content-label-desc">
              <div>고객 제공 안함</div>
              <div> / 최초 예상 금액</div>
            </div>
          </div>
          <div className="content-field-value full flex items-center gap-x-2">
            <div className="grow">
              <NumberBox
                value={hCostResult.conEstimatedTotalMaterialCost}
                placeholder="자재비"
                format="#,##0원"
                inputAttr={{ class: "text-right" }}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.conEstimatedTotalMaterialCost = value;
                  draft.conEstimatedTotalAmount = value + draft.conEstimatedTotalConstructionCost;
                })}
              />
              <div className="content-value-desc"></div>
            </div>
            <div className="">+</div>
            <div>
              <NumberBox
                value={hCostResult.conEstimatedTotalConstructionCost}
                placeholder="시공비"
                format="#,##0원"
                inputAttr={{ class: "text-right" }}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.conEstimatedTotalConstructionCost = value;
                  draft.conEstimatedTotalAmount = value + draft.conEstimatedTotalMaterialCost;
                })}
              />
              <div className="content-value-desc"></div>
            </div>
            <div className="">=</div>
            <div>
              <NumberBox
                value={hCostResult.conEstimatedTotalAmount}
                placeholder="총 금액"
                format="#,##0원"
                inputAttr={{ class: "text-right" }}
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.conEstimatedTotalAmount = value;
                })}
              />
              <div className="content-value-desc"></div>
            </div>
          </div>
        </div>
      </div>
      <AlertModal
        visible={alertModalVisible}
        modalMode={"confirm"}
        type={"counselFacilityType"}
        alertCallback={e => alertCallback(e)}
      />
    </>
  );
};

export default CounselPopupDetailV3;
